/**
 * AWARE Health — RiskBadge Component
 * Pill badge for risk categories: low / medium / high.
 * Uses translations for labels. Theme-aware.
 */

import { useTranslation } from 'react-i18next'
import { useViewMode } from '../../contexts/ViewModeContext'
import type { RiskCategory } from '../../types/forecast'

interface RiskBadgeProps {
  risk: RiskCategory
  size?: 'sm' | 'md'
}

const CONFIG: Record<RiskCategory, { color: string; bg: string; dot: string }> = {
  low:    { color: '#10b981', bg: 'rgba(16,185,129,0.12)',  dot: '#10b981' },
  medium: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', dot: '#f59e0b' },
  high:   { color: '#ef4444', bg: 'rgba(239,68,68,0.12)',  dot: '#ef4444' },
}

export default function RiskBadge({ risk, size = 'md' }: RiskBadgeProps) {
  const { t } = useTranslation()
  const { isDetailed } = useViewMode()
  const cfg = CONFIG[risk] ?? CONFIG.low
  const px = size === 'sm' ? '6px 10px' : '4px 12px'
  const fontSize = size === 'sm' ? '10px' : '11px'

  // In simple view use full "Low Risk" / "High Risk"; in detailed use short "Low" / "High"
  const label = isDetailed
    ? t(`risk.${risk}Short`)
    : (size === 'sm' ? t(`risk.${risk}Short`) : t(`risk.${risk}`))

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: px,
        borderRadius: 20,
        fontSize,
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.color}33`,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: size === 'sm' ? 5 : 6,
          height: size === 'sm' ? 5 : 6,
          borderRadius: '50%',
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      {label}
    </span>
  )
}
